import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Upload, Save, Trash2, Loader2, CheckCircle2, ShieldCheck, Smartphone, Mail, ArrowLeft } from "lucide-react";
import { API, formatApiError } from "../lib/api";

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

const Shell = ({ children }) => (
    <div className="min-h-screen bg-moebus-surface py-12 md:py-16 px-4">
        <div className="max-w-2xl mx-auto">
            <div className="text-xs tracking-[0.3em] uppercase text-moebus-green font-semibold mb-2 text-center">
                Möbus Umweltschutz GmbH
            </div>
            {children}
        </div>
    </div>
);

function OtpGate({ token, authInfo, onVerified }) {
    const smsAvailable = authInfo.channels.sms.available;
    const [channel, setChannel] = useState(smsAvailable ? "sms" : "email");
    const [step, setStep] = useState("choose");
    const [code, setCode] = useState("");
    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [sentTo, setSentTo] = useState("");

    const sendCode = async (ch) => {
        setSending(true);
        try {
            const { data } = await axios.post(`${API}/manage/${token}/request-otp`, { channel: ch });
            setSentTo(data.sent_to);
            setStep("code");
            setCode("");
            toast.success(ch === "sms" ? "Code per SMS gesendet." : "Code per E-Mail gesendet.");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Code konnte nicht gesendet werden.");
        } finally {
            setSending(false);
        }
    };

    const verify = async () => {
        if (code.trim().length < 4) {
            toast.error("Bitte gib den 6-stelligen Code ein.");
            return;
        }
        setVerifying(true);
        try {
            const { data } = await axios.post(`${API}/manage/${token}/verify-otp`, { channel, code: code.trim() });
            onVerified(data.session);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Verifizierung fehlgeschlagen.");
        } finally {
            setVerifying(false);
        }
    };

    const ChannelBtn = ({ ch, icon: Icon, label, masked, testId }) => (
        <button
            data-testid={testId}
            onClick={() => setChannel(ch)}
            className={`w-full flex items-center gap-3 border rounded-lg px-4 py-3 text-left transition-colors ${
                channel === ch ? "border-moebus-green bg-emerald-50" : "border-moebus-border bg-white hover:border-slate-300"
            }`}
        >
            <Icon size={18} className={channel === ch ? "text-moebus-green" : "text-slate-400"} />
            <span className="grid">
                <span className="text-sm font-medium text-moebus-deep">{label}</span>
                <span className="text-xs text-slate-500">{masked}</span>
            </span>
            <span className={`ml-auto w-4 h-4 rounded-full border-2 ${channel === ch ? "border-moebus-green bg-moebus-green" : "border-slate-300"}`} />
        </button>
    );

    return (
        <div data-testid="otp-gate" className="bg-white border border-moebus-border rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-2">
                <ShieldCheck size={24} className="text-moebus-green" />
                <h1 className="font-heading font-bold text-2xl text-moebus-deep">Identität bestätigen</h1>
            </div>
            <p className="text-sm text-slate-500 mb-5">
                Zum Schutz deiner Daten senden wir dir einen 6-stelligen Bestätigungscode.
            </p>

            {step === "choose" && (
                <div className="grid gap-3">
                    {smsAvailable && (
                        <ChannelBtn ch="sms" icon={Smartphone} label="Per SMS" masked={authInfo.channels.sms.masked} testId="otp-channel-sms" />
                    )}
                    <ChannelBtn ch="email" icon={Mail} label="Per E-Mail" masked={authInfo.channels.email.masked} testId="otp-channel-email" />
                    <button
                        data-testid="otp-send-btn"
                        onClick={() => sendCode(channel)}
                        disabled={sending}
                        className="mt-1 inline-flex items-center justify-center gap-2 bg-moebus-green hover:bg-emerald-700 text-white font-medium px-4 py-3 rounded-md text-sm disabled:opacity-60"
                    >
                        {sending ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
                        {sending ? "Wird gesendet…" : "Code senden"}
                    </button>
                </div>
            )}

            {step === "code" && (
                <div className="grid gap-3">
                    <p data-testid="otp-sent-info" className="text-sm text-slate-600 bg-slate-50 border border-moebus-border rounded-md px-3 py-2">
                        Code gesendet an <span className="font-medium text-moebus-deep">{sentTo}</span>
                    </p>
                    <input
                        data-testid="otp-code-input"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        onKeyDown={(e) => e.key === "Enter" && verify()}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="••••••"
                        className="text-center text-2xl tracking-[0.5em] font-semibold border border-moebus-border rounded-md px-3 py-3"
                        autoFocus
                    />
                    <button
                        data-testid="otp-verify-btn"
                        onClick={verify}
                        disabled={verifying || code.length < 6}
                        className="inline-flex items-center justify-center gap-2 bg-moebus-green hover:bg-emerald-700 text-white font-medium px-4 py-3 rounded-md text-sm disabled:opacity-60"
                    >
                        {verifying ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                        {verifying ? "Wird geprüft…" : "Bestätigen"}
                    </button>
                    <div className="flex items-center justify-between text-xs">
                        <button data-testid="otp-back-btn" onClick={() => setStep("choose")}
                            className="inline-flex items-center gap-1 text-slate-500 hover:text-moebus-deep">
                            <ArrowLeft size={12} /> Anderen Weg wählen
                        </button>
                        <button data-testid="otp-resend-btn" onClick={() => sendCode(channel)} disabled={sending}
                            className="text-moebus-green font-medium hover:underline disabled:opacity-60">
                            Neuen Code anfordern
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function ManageApplicationPage() {
    const { token } = useParams();
    const sessionKey = `manage_session_${token}`;
    const [session, setSession] = useState(() => sessionStorage.getItem(sessionKey) || "");
    const [authInfo, setAuthInfo] = useState(null);
    const [app, setApp] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [withdrawn, setWithdrawn] = useState(false);
    const [phone, setPhone] = useState("");
    const [city, setCity] = useState("");
    const [message, setMessage] = useState("");
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    const authHeaders = session ? { "X-Manage-Session": session } : {};

    const expireSession = useCallback(() => {
        sessionStorage.removeItem(sessionKey);
        setSession("");
        setApp(null);
        toast.error("Deine Sitzung ist abgelaufen. Bitte bestätige dich erneut.");
    }, [sessionKey]);

    const loadAuthInfo = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/manage/${token}/auth-info`);
            setAuthInfo(data);
        } catch {
            setNotFound(true);
        }
    }, [token]);

    const loadApp = useCallback(async (sess) => {
        try {
            const { data } = await axios.get(`${API}/manage/${token}`, { headers: { "X-Manage-Session": sess } });
            setApp(data);
            setPhone(data.phone);
            setCity(data.city);
            setMessage(data.message || "");
        } catch (e) {
            if (e?.response?.status === 401) {
                sessionStorage.removeItem(sessionKey);
                setSession("");
                loadAuthInfo();
            } else {
                setNotFound(true);
            }
        }
    }, [token, sessionKey, loadAuthInfo]);

    useEffect(() => {
        document.title = "Bewerbung verwalten – Möbus Umweltschutz GmbH";
        if (session) loadApp(session);
        else loadAuthInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onVerified = (sess) => {
        sessionStorage.setItem(sessionKey, sess);
        setSession(sess);
        loadApp(sess);
    };

    const save = async () => {
        if (!phone.trim() || !city.trim()) {
            toast.error("Telefon und Ort dürfen nicht leer sein.");
            return;
        }
        setSaving(true);
        try {
            await axios.put(`${API}/manage/${token}`, { phone, city, message }, { headers: authHeaders });
            toast.success("Deine Angaben wurden aktualisiert.");
        } catch (e) {
            if (e?.response?.status === 401) return expireSession();
            toast.error(formatApiError(e?.response?.data?.detail) || "Speichern fehlgeschlagen.");
        } finally {
            setSaving(false);
        }
    };

    const upload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        const form = new FormData();
        files.forEach((f) => form.append("files", f));
        setUploading(true);
        try {
            const { data } = await axios.post(`${API}/manage/${token}/files`, form, { headers: authHeaders });
            setApp({ ...app, files: data.files });
            toast.success("Unterlagen erfolgreich hochgeladen. Vielen Dank!");
        } catch (err) {
            if (err?.response?.status === 401) return expireSession();
            toast.error(formatApiError(err?.response?.data?.detail) || "Upload fehlgeschlagen.");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const withdraw = async () => {
        if (!window.confirm("Möchtest du deine Bewerbung wirklich zurückziehen? Deine Daten und Unterlagen werden endgültig gelöscht. Du erhältst eine Bestätigung per E-Mail.")) return;
        try {
            await axios.delete(`${API}/manage/${token}`, { headers: authHeaders });
            sessionStorage.removeItem(sessionKey);
            setWithdrawn(true);
        } catch (e) {
            if (e?.response?.status === 401) return expireSession();
            toast.error(formatApiError(e?.response?.data?.detail) || "Zurückziehen fehlgeschlagen.");
        }
    };

    if (withdrawn) {
        return (
            <Shell>
                <div data-testid="withdraw-success" className="bg-white border border-moebus-border rounded-xl p-8 text-center">
                    <CheckCircle2 size={40} className="text-moebus-green mx-auto mb-4" />
                    <h1 className="font-heading font-bold text-2xl text-moebus-deep">Bewerbung zurückgezogen</h1>
                    <p className="mt-3 text-slate-600">
                        Deine Bewerbung wurde zurückgezogen – du erhältst dazu eine Bestätigung per E-Mail.
                        Deine Daten und Unterlagen werden endgültig gelöscht.
                    </p>
                    <a href="/" className="inline-block mt-5 text-sm text-moebus-green font-medium hover:underline">Zur Startseite</a>
                </div>
            </Shell>
        );
    }

    if (notFound) {
        return (
            <Shell>
                <div data-testid="manage-not-found" className="bg-white border border-moebus-border rounded-xl p-8 text-center">
                    <h1 className="font-heading font-bold text-2xl text-moebus-deep">Link nicht gültig</h1>
                    <p className="mt-3 text-slate-600">
                        Diese Bewerbung wurde nicht gefunden oder der Link ist nicht mehr gültig.
                    </p>
                    <a href="/" className="inline-block mt-5 text-sm text-moebus-green font-medium hover:underline">Zur Startseite</a>
                </div>
            </Shell>
        );
    }

    if (!session || !app) {
        if (!session && authInfo) {
            return <Shell><OtpGate token={token} authInfo={authInfo} onVerified={onVerified} /></Shell>;
        }
        return <Shell><div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div></Shell>;
    }

    return (
        <Shell>
            <div data-testid="manage-page" className="grid gap-5">
                <div className="bg-white border border-moebus-border rounded-xl p-6">
                    <h1 className="font-heading font-bold text-2xl text-moebus-deep">
                        Hallo {app.first_name}, hier verwaltest du deine Bewerbung
                    </h1>
                    <p className="mt-2 text-sm text-slate-500">
                        Bereich: <span className="font-medium text-moebus-deep">{app.position}</span> · Status:{" "}
                        <span className="font-medium text-moebus-deep">{app.status}</span>
                    </p>
                </div>

                <div className="bg-white border border-moebus-border rounded-xl p-6">
                    <h2 className="font-heading font-semibold text-lg text-moebus-deep mb-3">Deine Unterlagen</h2>
                    {app.files.length === 0 ? (
                        <p data-testid="manage-no-files" className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
                            Du hast noch keine Unterlagen hochgeladen. Bitte reiche mindestens deinen Lebenslauf nach.
                        </p>
                    ) : (
                        <ul className="grid gap-1.5 mb-3">
                            {app.files.map((f) => (
                                <li key={f.id} data-testid="manage-file-item" className="flex items-center gap-2 text-sm text-slate-700">
                                    <FileText size={15} className="text-moebus-green flex-shrink-0" />
                                    {f.original_filename} <span className="text-xs text-slate-400">({fmtSize(f.size)})</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {app.files.length < app.max_files && (
                        <label className="inline-flex items-center gap-2 bg-moebus-green hover:bg-emerald-700 text-white font-medium px-4 py-2.5 rounded-md text-sm cursor-pointer">
                            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                            {uploading ? "Wird hochgeladen…" : "Unterlagen hochladen"}
                            <input
                                data-testid="manage-file-input"
                                type="file"
                                multiple
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                onChange={upload}
                                disabled={uploading}
                                className="hidden"
                            />
                        </label>
                    )}
                    <p className="mt-2 text-xs text-slate-400">
                        PDF, Word oder Bilder · max. {app.max_files} Dateien insgesamt · je max. 10 MB
                    </p>
                </div>

                <div className="bg-white border border-moebus-border rounded-xl p-6 grid gap-3">
                    <h2 className="font-heading font-semibold text-lg text-moebus-deep">Angaben bearbeiten</h2>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <label className="grid gap-1 text-xs font-medium text-slate-500">
                            Telefon
                            <input data-testid="manage-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
                                className="text-sm font-normal border border-moebus-border rounded-md px-3 py-2" />
                        </label>
                        <label className="grid gap-1 text-xs font-medium text-slate-500">
                            Wohnort
                            <input data-testid="manage-city-input" value={city} onChange={(e) => setCity(e.target.value)}
                                className="text-sm font-normal border border-moebus-border rounded-md px-3 py-2" />
                        </label>
                    </div>
                    <label className="grid gap-1 text-xs font-medium text-slate-500">
                        Nachricht an uns
                        <textarea data-testid="manage-message-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
                            className="text-sm font-normal border border-moebus-border rounded-md px-3 py-2" />
                    </label>
                    <button
                        data-testid="manage-save-btn"
                        onClick={save}
                        disabled={saving}
                        className="inline-flex items-center gap-2 self-start bg-moebus-deep hover:bg-moebus-deep/90 text-white font-medium px-4 py-2.5 rounded-md text-sm disabled:opacity-60"
                    >
                        <Save size={14} /> {saving ? "Speichern…" : "Änderungen speichern"}
                    </button>
                </div>

                <div className="bg-white border border-red-200 rounded-xl p-6">
                    <h2 className="font-heading font-semibold text-lg text-red-700">Bewerbung zurückziehen</h2>
                    <p className="mt-1.5 text-sm text-slate-500">
                        Wenn du deine Bewerbung zurückziehst, werden deine Daten und Unterlagen endgültig gelöscht.
                        Du erhältst eine Bestätigung per E-Mail.
                    </p>
                    <button
                        data-testid="manage-withdraw-btn"
                        onClick={withdraw}
                        className="mt-3 inline-flex items-center gap-2 border border-red-300 text-red-600 hover:bg-red-50 font-medium px-4 py-2.5 rounded-md text-sm"
                    >
                        <Trash2 size={14} /> Bewerbung endgültig zurückziehen
                    </button>
                </div>
            </div>
        </Shell>
    );
}
